import { useCallback, useRef } from 'react';
import { showError, showSuccessAfterModalClose } from '../../../shared/ui/appToast';
import { reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import type { NavigationProp } from '@react-navigation/native';
import { navigateInShell } from '../../../shell/navigation/rootNavigation';
import { executeVoidSaleCommand, formatSaleShortRefSuffix, formatSaleDisplayRef, getSaleDetail, openSaleDetail, DEFAULT_PAYMENT_METHOD_LABELS } from '../../../sales/public-api';
import { useMercadoPagoQrSession } from '../../../mercadopago/application/useMercadoPagoQrSession';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import {
  printSaleReceiptFromDetail,
} from '../../../printing/application/printSaleTicket';
import {
  printFiscalComprobante,
  type FiscalReceiptPrintInput,
} from '../../../printing/application/printFiscalComprobante';
import { fetchSalePickupTrackingUrl } from '../../../printing/infra/pickupTrackingRepo';
import { shouldAutoPrintSaleReceipt } from '../../../printing/application/printOrchestrator';
import { formatSaleFulfillmentLabel } from '../../../sales/application/salePresentation';
import { emitFiscalInvoiceAwaitingCae, getFiscalContext } from '../../../fiscal/infra-api';
import { formatPosMoney } from '../../application/posMoneyFormat';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';

export function usePosMpQrActions(options: {
  navigation: NavigationProp<Record<string, object | undefined>>;
  currentBranchId: string | null | undefined;
  lastMpQrSaleId: string | null;
  userId: string | null | undefined;
  clearCart: () => void;
  resetCheckout: () => void;
  closeQrModal: () => void;
  setCheckoutVisible: (v: boolean) => void;
  clearPersistenceAfterSale: () => void | Promise<void>;
  /**
   * Descarta el saleId pendiente del checkout. Hay que llamarlo al anular la venta QR
   * (cambio de medio / anulación) para que el próximo cobro genere un saleId nuevo en
   * vez de reusar el anulado (causa sale_already_voided o idempotent silencioso = pérdida).
   */
  clearPendingSaleId: () => void;
  onMpQrApproved?: () => void;
}) {
  const {
    navigation,
    currentBranchId,
    lastMpQrSaleId,
    userId,
    clearCart,
    resetCheckout,
    closeQrModal,
    setCheckoutVisible,
    clearPersistenceAfterSale,
    clearPendingSaleId,
    onMpQrApproved,
  } = options;

  const kitchenTicketsEnabled = useHasBusinessCapability('kitchen_tickets');

  const showQrModal = usePosCheckoutStore((s) => s.showQrModal);
  const lastMpQrAmount = usePosCheckoutStore((s) => s.lastMpQrAmount);
  const lastMpQrFiscalMode = usePosCheckoutStore((s) => s.lastMpQrFiscalMode);

  const isFinalizingRef = useRef(false);
  const isChangingPaymentRef = useRef(false);
  const isVoidingRef = useRef(false);

  const handleMpQrApproved = useCallback(async () => {
    if (!lastMpQrSaleId || !currentBranchId) return;
    try {
      const detail = await getSaleDetail(lastMpQrSaleId);
      if (!detail) return;
      const fallbackTotal = Number.isFinite(detail.total) ? detail.total : 0;
      const amount = lastMpQrAmount != null && Number.isFinite(lastMpQrAmount) && lastMpQrAmount > 0
        ? lastMpQrAmount
        : fallbackTotal;
      // Venta fiscal por QR: con el pago confirmado emitimos (o recuperamos el
      // comprobante ya emitido — la EF es idempotente) e imprimimos la factura.
      // Si ARCA no responde a tiempo, sale el ticket interno con leyenda.
      let saleTrackingUrl: string | null = null;
      try {
        saleTrackingUrl = await fetchSalePickupTrackingUrl(lastMpQrSaleId);
      } catch {
        saleTrackingUrl = null;
      }
      let fiscalPrintInput: FiscalReceiptPrintInput | null = null;
      if (lastMpQrFiscalMode === 'fiscal') {
        const fiscalAwait = await emitFiscalInvoiceAwaitingCae(lastMpQrSaleId);
        const emission = fiscalAwait.emission;
        if (emission?.cae?.trim()) {
          const emitterSrc = emission.emitter ?? null;
          const profile = emitterSrc
            ? null
            : (await getFiscalContext(currentBranchId).catch(() => null))?.profile;
          fiscalPrintInput = {
            invoiceType: emission.invoiceType ?? detail.invoiceType,
            pointOfSale: emission.pointOfSale ?? detail.invoicePointOfSale,
            invoiceNumber: emission.invoiceNumber ?? detail.invoiceNumber,
            cae: emission.cae,
            caeExpiration: emission.caeExpiration,
            issuedAt: emission.issuedAt,
            qrUrl: emission.qrUrl,
            buyerName: emission.buyerName ?? detail.invoiceBuyerName,
            buyerCuit: emission.buyerCuit ?? detail.invoiceBuyerCuit,
            buyerTaxCondition: emission.buyerTaxCondition,
            items: detail.items.map((item) => ({
              label: item.label ?? item.productId,
              quantity: item.quantity,
              price: item.price,
            })),
            tipAmount: Math.max(0, Number(detail.tipAmount ?? 0)),
            total: emission.totalAmount ?? detail.total,
            netAmount: emission.netAmount,
            ivaAmount: emission.ivaAmount,
            saleRef: formatSaleDisplayRef({
              saleId: detail.id,
              counterNumber: detail.counterNumber,
            }),
            copyLabel: 'ORIGINAL',
            emitter: {
              businessName: emitterSrc?.businessName ?? profile?.businessName ?? detail.emitterBusinessName,
              cuit: emitterSrc?.cuit ?? profile?.emitterCuit ?? detail.emitterCuit,
              taxRegime: emitterSrc?.taxRegime ?? profile?.taxRegime,
              domicilioComercial: emitterSrc?.domicilioComercial ?? profile?.domicilioComercial,
              iibb: emitterSrc?.iibb ?? profile?.iibb,
              inicioActividades: emitterSrc?.inicioActividades ?? profile?.inicioActividades,
            },
            environment:
              emitterSrc?.environment ?? profile?.environment ?? detail.emitterFiscalEnvironment,
            salePaymentConditionLabel: 'Contado',
            trackingUrl: saleTrackingUrl,
            fulfillmentLabel: kitchenTicketsEnabled
              ? formatSaleFulfillmentLabel(detail.fulfillmentType ?? null)
              : null,
          };
        }
      }
      const isFiscal = lastMpQrFiscalMode === 'fiscal';
      const approvedFiscalInput = fiscalPrintInput;
      const auto = await shouldAutoPrintSaleReceipt(currentBranchId);
      const printSaleDoc = (autoDialog: boolean) =>
        approvedFiscalInput
          ? () =>
              printFiscalComprobante({
                branchId: currentBranchId,
                input: approvedFiscalInput,
              }).catch((err) => console.warn('[POS] MP QR fiscal print failed', err))
          : () =>
              printSaleReceiptFromDetail({
                detail,
                paymentMethodNames: DEFAULT_PAYMENT_METHOD_LABELS,
                kitchenTickets: kitchenTicketsEnabled,
                footerNotes: isFiscal
                  ? ['Factura en emisión — se imprime desde Ventas']
                  : undefined,
                autoOpenPrintDialog: autoDialog,
              }).catch((err) => console.warn('[POS] MP QR receipt print failed', err));
      showSuccessAfterModalClose(
        'Cobro confirmado',
        fiscalPrintInput
          ? `${formatPosMoney(amount)} · Imprimir la factura`
          : `${formatPosMoney(amount)} · Imprimir el ticket`,
        7000,
        () => {
          void printSaleDoc(false)();
        }
      );
      if (auto) {
        void printSaleDoc(true)();
      }
      onMpQrApproved?.();
    } catch (error) {
      reportUserVisibleFailure({ module: 'pos', op: 'pos.mpqr_approved_followup', error, title: 'MP QR approved follow-up failed' });
      console.warn('[POS] MP QR approved follow-up failed', error);
    }
  }, [currentBranchId, kitchenTicketsEnabled, lastMpQrAmount, lastMpQrSaleId, lastMpQrFiscalMode, onMpQrApproved]);

  const mpQrSession = useMercadoPagoQrSession({
    saleId: lastMpQrSaleId,
    visible: showQrModal,
    amount: lastMpQrAmount,
    saleRef: lastMpQrSaleId ? formatSaleShortRefSuffix(lastMpQrSaleId) : null,
    fiscalMode: lastMpQrFiscalMode,
    onApproved: () => {
      void handleMpQrApproved();
    },
  });

  const finalizeMpQrPosSession = useCallback(() => {
    if (isFinalizingRef.current) return;
    isFinalizingRef.current = true;
    try {
    clearCart();
    resetCheckout();
    closeQrModal();
    void clearPersistenceAfterSale();
    } finally {
      isFinalizingRef.current = false;
    }
  }, [clearCart, clearPersistenceAfterSale, closeQrModal, resetCheckout]);

  const handleConfigureMercadoPagoFromQr = useCallback(() => {
    closeQrModal();
    navigateInShell(navigation, 'MercadoPagoBranchSettings');
  }, [closeQrModal, navigation]);

  const handleChangePaymentMethodFromQr = useCallback(async () => {
    if (isChangingPaymentRef.current) return;
    isChangingPaymentRef.current = true;
    try {
    if (!lastMpQrSaleId || !currentBranchId) {
      closeQrModal();
      setCheckoutVisible(true);
      return;
    }
      await executeVoidSaleCommand({
        saleId: lastMpQrSaleId,
        branchId: currentBranchId,
        voidReason: 'Cambio de medio de pago desde POS',
        voidedByUserId: userId ?? null,
      });
      // La venta QR quedó anulada: descartar el saleId pendiente para que el próximo
      // cobro (con otro medio) genere una venta nueva en vez de reusar la anulada.
      clearPendingSaleId();
      closeQrModal();
      setCheckoutVisible(true);
    } catch (error: unknown) {
      reportUserVisibleFailure({ module: 'pos', op: 'pos.mpqr_change_payment', error, title: 'No se pudo cambiar el medio.' });
      const message = error instanceof Error ? error.message : 'No se pudo anular la venta pendiente.';
      showError('No se pudo cambiar el medio', message);
    } finally {
      isChangingPaymentRef.current = false;
    }
  }, [closeQrModal, currentBranchId, lastMpQrSaleId, setCheckoutVisible, userId, clearPendingSaleId]);

  const handleOpenMpQrSaleInVentas = useCallback(() => {
    if (!lastMpQrSaleId) return;
    finalizeMpQrPosSession();
    openSaleDetail(navigation, { saleId: lastMpQrSaleId });
  }, [finalizeMpQrPosSession, lastMpQrSaleId, navigation]);

  const handleVoidMpQrSale = useCallback(async () => {
    if (isVoidingRef.current) return;
    isVoidingRef.current = true;
    try {
    if (!lastMpQrSaleId || !currentBranchId) {
      finalizeMpQrPosSession();
      return;
    }
      await executeVoidSaleCommand({
        saleId: lastMpQrSaleId,
        branchId: currentBranchId,
        voidReason: 'Anulación desde cobro QR Mercado Pago',
        voidedByUserId: userId ?? null,
      });
      // La venta QR quedó anulada: descartar el saleId pendiente por si se reabre el checkout.
      clearPendingSaleId();
      finalizeMpQrPosSession();
    } catch (error: unknown) {
      reportUserVisibleFailure({ module: 'pos', op: 'pos.mpqr_void', error, title: 'No se pudo anular la venta.' });
      const message = error instanceof Error ? error.message : 'No se pudo anular la venta.';
      showError('No se pudo anular la venta', message);
    } finally {
      isVoidingRef.current = false;
    }
  }, [currentBranchId, finalizeMpQrPosSession, lastMpQrSaleId, userId, clearPendingSaleId]);

  return {
    mpQrSession,
    finalizeMpQrPosSession,
    handleConfigureMercadoPagoFromQr,
    handleChangePaymentMethodFromQr,
    handleOpenMpQrSaleInVentas,
    handleVoidMpQrSale,
  };
}
